/**
 * SparklineCell — inline miniature trend chart.
 *
 * Used inside Table cells and KPICards. No axes, no tooltip — just the
 * shape of the recent trend. Optional reference range drawn as a faint
 * band; optional "spot" dot at the last point.
 */

import { useMemo } from "react";
import {
  Area,
  AreaChart,
  ReferenceArea,
  ReferenceDot,
  ResponsiveContainer,
} from "recharts";
import type { TimeseriesPoint } from "../types";
import { SERIES_PALETTE } from "../series-palette";

export interface SparklineCellProps {
  points: TimeseriesPoint[];
  /** Pixel height (number), or a CSS length like "100%" to fill the parent. Default 28. */
  height?: number | string;
  /** Token-class for the line. Default brand. */
  colorClass?: string;
  /** Reference range — drawn as a faint band. */
  referenceRange?: { min: number; max: number };
  /** Show a dot at the final point. Default true. */
  showSpot?: boolean;
  /** Optional aria-label for screen readers. */
  ariaLabel?: string;
}

export function SparklineCell({
  points,
  height = 28,
  referenceRange,
  showSpot = true,
  ariaLabel,
}: SparklineCellProps) {
  const data = useMemo(
    () =>
      points
        .filter((p) => p.v !== null && Number.isFinite(p.v))
        .map((p) => ({ t: p.t, v: p.v as number })),
    [points],
  );

  if (data.length === 0) {
    return (
      <div
        aria-label={ariaLabel}
        className="h-7 w-full rounded bg-surface-muted/50"
        style={{ height }}
      />
    );
  }

  const last = data[data.length - 1];

  return (
    <div className="w-full" style={{ height }} aria-label={ariaLabel}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart
          data={data}
          margin={{ top: 2, right: 2, bottom: 2, left: 2 }}
        >
          <defs>
            <linearGradient id="sparkline-fill" x1="0" y1="0" x2="0" y2="1">
              <stop
                offset="0%"
                stopColor={SERIES_PALETTE[0]}
                stopOpacity={0.3}
              />
              <stop
                offset="100%"
                stopColor={SERIES_PALETTE[0]}
                stopOpacity={0}
              />
            </linearGradient>
          </defs>
          {referenceRange && (
            <ReferenceArea
              y1={referenceRange.min}
              y2={referenceRange.max}
              fill="rgb(var(--color-on-surface-muted))"
              fillOpacity={0.06}
              stroke="none"
            />
          )}
          <Area
            type="monotone"
            dataKey="v"
            stroke={SERIES_PALETTE[0]}
            strokeWidth={1.6}
            fill="url(#sparkline-fill)"
            isAnimationActive={false}
            dot={false}
          />
          {showSpot && (
            <ReferenceDot
              x={last.t}
              y={last.v}
              r={2.5}
              fill={SERIES_PALETTE[0]}
              stroke="rgb(var(--color-surface))"
              strokeWidth={1.5}
              isFront
            />
          )}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
